<script setup lang="ts">
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/vue-3';

// eslint-disable-next-line vue/prop-name-casing
const props = defineProps<NodeViewProps>();

const containerRef = ref<HTMLElement>();
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

const mediaType = computed(() => props.node.type.name);
const isImage = computed(() => mediaType.value === 'image');

const currentWidth = computed(() => {
  const w = props.node.attrs.width;
  if (!w) {
    return null;
  }
  if (typeof w === 'string' && w.endsWith('%')) {
    return w;
  }

  return `${w}%`;
});

const containerStyle = computed(() => {
  if (currentWidth.value) {
    return { width: currentWidth.value, maxWidth: '100%' };
  }

  return { maxWidth: '100%' };
});

function getEditorContentWidth(): number {
  const editorEl = containerRef.value?.closest('.ProseMirror');

  return editorEl?.clientWidth ?? 600;
}

function onResizeStart(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  isResizing.value = true;
  startX.value = event.clientX;
  startWidth.value = containerRef.value?.offsetWidth ?? 200;

  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(event: MouseEvent) {
  if (!isResizing.value) {
    return;
  }
  const delta = event.clientX - startX.value;
  const newPx = Math.max(80, startWidth.value + delta);
  if (containerRef.value) {
    containerRef.value.style.width = `${newPx}px`;
  }
}

function onResizeEnd() {
  if (!isResizing.value) {
    return;
  }
  isResizing.value = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);

  const finalPx = containerRef.value?.offsetWidth;
  if (finalPx) {
    const editorWidth = getEditorContentWidth();
    const pct = Math.round((finalPx / editorWidth) * 100);
    const clampedPct = Math.max(10, Math.min(100, pct));
    props.updateAttributes({ width: `${clampedPct}%` });
  }
}

function setPresetWidth(preset: '100%' | '75%' | '50%' | null) {
  props.updateAttributes({ width: preset });
  // Re-select the node so the presets toolbar stays visible
  nextTick(() => {
    const pos = props.getPos();
    if (typeof pos === 'number') {
      props.editor.commands.setNodeSelection(pos);
    }
  });
}

onUnmounted(() => {
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
});
</script>

<template>
  <NodeViewWrapper
    class="resizable-media"
    :class="{ 'is-selected': selected, 'is-resizing': isResizing }"
  >
    <div ref="containerRef" class="resizable-container" :style="containerStyle">
      <img
        v-if="isImage"
        :src="node.attrs.src"
        :alt="node.attrs.alt"
        :title="node.attrs.title"
        class="resizable-image"
        draggable="false"
      />
      <div v-else class="resizable-iframe-wrapper">
        <iframe :src="node.attrs.src" frameborder="0" allowfullscreen />
      </div>

      <div
        class="resize-handle resize-handle-right"
        @mousedown="onResizeStart"
      />

      <div v-if="selected" class="resize-presets">
        <button
          type="button"
          class="preset-btn"
          :class="{ active: node.attrs.width === '100%' }"
          @mousedown.prevent.stop
          @click.stop="setPresetWidth('100%')"
        >
          100%
        </button>
        <button
          type="button"
          class="preset-btn"
          :class="{ active: node.attrs.width === '75%' }"
          @mousedown.prevent.stop
          @click.stop="setPresetWidth('75%')"
        >
          75%
        </button>
        <button
          type="button"
          class="preset-btn"
          :class="{ active: node.attrs.width === '50%' }"
          @mousedown.prevent.stop
          @click.stop="setPresetWidth('50%')"
        >
          50%
        </button>
        <button
          type="button"
          class="preset-btn"
          :class="{ active: !node.attrs.width }"
          @mousedown.prevent.stop
          @click.stop="setPresetWidth(null)"
        >
          {{ $t('wysiwygEditor.original') }}
        </button>
      </div>
    </div>
  </NodeViewWrapper>
</template>

<style scoped lang="scss">
.resizable-media {
  display: block;
  max-width: 100%;
  line-height: 0;
}

.resizable-container {
  position: relative;
  display: block;
  max-width: 100%;
  line-height: 0;
}

.resizable-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: var(--border-radius-4);
}

.resizable-iframe-wrapper {
  width: 100%;

  iframe {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;
    border-radius: var(--border-radius-4);
  }
}

.resize-handle {
  position: absolute;
  width: 16px;
  top: 0;
  bottom: 0;
  right: -8px;
  cursor: ew-resize;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-standard);
  z-index: 5;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 40px;
    background-color: var(--color-primary, #2563eb);
    border-radius: 3px;
    box-shadow: 0 0 4px rgba(37, 99, 235, 0.4);
  }
}

.resizable-container:hover .resize-handle,
.is-selected .resize-handle,
.is-resizing .resize-handle {
  opacity: 1;
}

.is-selected .resizable-container {
  outline: 2px solid var(--color-primary, #2563eb);
  outline-offset: 2px;
  border-radius: var(--border-radius-4);
}

.is-resizing {
  cursor: ew-resize;
  user-select: none;
}

.resize-presets {
  position: absolute;
  bottom: -36px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: var(--space-2);
  background-color: var(--color-gray-800, #1f2937);
  border-radius: var(--border-radius-6);
  padding: var(--space-2);
  z-index: 10;
  box-shadow: var(--shadow-lg);
  // The wrappers set line-height 0 (no gap under the image); without its own,
  // each button was 8px tall with its text spilling out. 16px makes it 24px.
  line-height: var(--line-height-16);
  // A dark bar: the ring is white, and inside the buttons, which fill it.
  --color-focus-ring: var(--color-gray-0);
}

.preset-btn {
  padding: var(--space-4) var(--space-8);
  font-size: var(--font-size-11);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-300, #d1d5db);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-4);
  cursor: pointer;
  white-space: nowrap;
  @include focus-ring($inset: true);

  &:hover {
    color: #fff;
    background-color: var(--color-gray-700, #374151);
  }

  &.active {
    color: #fff;
    background-color: var(--color-primary, #2563eb);
  }
}
</style>
